import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useState, useMemo, Fragment, memo } from "react"
import { LoaderCircleIcon, TrashIcon, ExternalLinkIcon, TagIcon, SearchIcon, EditIcon, PlusIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData, useNavigate, useSearchParams } from "react-router"
import type { LoaderFunctionArgs, ActionFunctionArgs } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type { Lens, BrandUserGroup } from "@/utils/types"

const routeId = "brand-user-groups"
const sessionKey = "brand-user-groups-filter-storage"

interface GroupData {
  name: string
  internalName?: string
}

interface MyGroup {
  tenant: string
  product: string
  data: GroupData[]
}

interface ConfigGroupLens {
  name: string
  code: string
  order: number
}

interface ConfigAccessTag {
  name: string
  restrictContributors: boolean
  usage: string
}

const ACCESS_TAG_USAGE = ["both", "read", "write"] as const
const ACCESS_TAG_USAGE_LABELS = ["Both", "Read", "Write"] as const

function useGroupData() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  return data
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject =
    filters.parsed?.project == null || !projects.includes(filters.parsed.project) ? projects[0] : filters.parsed.project

  const [groupResponse, availableGroupsResponse] = await Promise.allSettled([
    fetchAPI<BrandUserGroup[]>(`/api/unityviewer/usergroup/${loadedOrganisation}/${loadedProject}`)
      .then(handleResponse)
      .catch(() => []),
    fetchAPI<MyGroup[]>(`/api/my/groups?organisation=${encodeURIComponent(loadedOrganisation)}`).then(handleResponse),
  ])

  const groups = groupResponse.status === "fulfilled" ? groupResponse.value : []
  const availableGroups = availableGroupsResponse.status === "fulfilled" ? availableGroupsResponse.value : []

  const result = {
    loadedOrganisation,
    projects,
    loadedProject,
    availableGroups,
    groups,
  }

  return result
}

async function action({ request }: ActionFunctionArgs) {
  try {
    const formData = await request.formData()
    const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

    switch (action) {
      case "ADD_GROUP": {
        const tenantProduct = formData.get("tenantProduct") as string
        if (tenantProduct.length === 0) return { action, errorMessage: "Tenant and Product are required" }

        const [tenant, product] = tenantProduct.split("::")
        if (tenant == null || tenant === "" || product == null || product === "") {
          return { action, errorMessage: "Invalid Tenant and Product format" }
        }

        const groupName = formData.get("groupName") as string
        if (groupName === "") return { action, errorMessage: "Group is required" }

        try {
          await fetchAPI(`/api/unityviewer/usergroup/${organisation}/${project}/${tenant}/${product}/${groupName}`, {
            method: "POST",
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error adding group" }
        }
      }
      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch (error) {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

async function mainAction({ request }: ActionFunctionArgs) {
  try {
    const formData = await request.formData()
    const { action, organisation, project, tenant, product, groupName } = Object.fromEntries(formData) as Record<
      string,
      string
    >

    switch (action) {
      case "DELETE_GROUP": {
        if (tenant === "") return { action, errorMessage: "Tenant is required" }
        if (product === "") return { action, errorMessage: "Product is required" }
        if (groupName === "") return { action, errorMessage: "Group is required" }

        try {
          await fetchAPI(`/api/unityviewer/usergroup/${organisation}/${project}/${tenant}/${product}/${groupName}`, {
            method: "DELETE",
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error adding group" }
        }
      }
      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch (error) {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

async function configLoader({ params }: LoaderFunctionArgs) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)
  const loadedProject =
    filters.parsed?.project == null || !projects.includes(filters.parsed.project) ? projects[0] : filters.parsed.project

  const [tenant, product, groupName] = params.groupId?.split("::") ?? []

  if (tenant === "" || tenant == null || product === "" || product == null || groupName === "" || groupName == null) {
    throw new Error("Invalid group ID format")
  }

  const [lensesResponse, accessTagsResponse, brandLensesResponse] = await Promise.allSettled([
    fetchAPI<ConfigGroupLens[]>(
      `/api/unityviewer/usergroup/lenses/${loadedOrganisation}/${loadedProject}/${tenant}/${product}/${groupName}`,
    )
      .then(handleResponse)
      .catch(() => []),
    fetchAPI<ConfigAccessTag[]>(
      `/api/unityviewer/usergroup/accessTags/${loadedOrganisation}/${loadedProject}/${tenant}/${product}/${groupName}`,
    )
      .then(handleResponse)
      .catch(() => []),
    fetchAPI<Lens[]>(`/api/unityviewer/lens/${loadedOrganisation}/${loadedProject}`)
      .then(handleResponse)
      .catch(() => []),
  ])

  const lenses = lensesResponse.status === "fulfilled" ? lensesResponse.value : []
  const accessTags = accessTagsResponse.status === "fulfilled" ? accessTagsResponse.value : []
  const brandLenses = brandLensesResponse.status === "fulfilled" ? brandLensesResponse.value : []

  const result = {
    loadedOrganisation,
    loadedProject,
    group: { tenant, product, groupName },
    lenses,
    accessTags,
    brandLenses,
  }

  return result
}

async function configAction({ request, params }: ActionFunctionArgs) {
  try {
    const formData = await request.formData()
    const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

    if (organisation === "") return { action, errorMessage: "Missing organisation" }
    if (project === "") return { action, errorMessage: "Missing project" }

    const [tenant, product, groupName] = params.groupId?.split("::") ?? []

    if (tenant === "" || tenant == null || product === "" || product == null || groupName === "" || groupName == null) {
      return { action, errorMessage: "Invalid group ID format" }
    }

    switch (action) {
      case "ADD_LENS": {
        const name = formData.get("name") as string
        if (name === "") return { action, errorMessage: "Lens name is required" }

        try {
          await fetchAPI(
            `/api/unityviewer/usergroup/lenses/${organisation}/${project}/${tenant}/${product}/${groupName}`,
            {
              method: "POST",
              body: { name },
            },
          ).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error adding lens" }
        }
      }

      case "REMOVE_LENS": {
        const lensName = formData.get("lensName") as string
        if (lensName === "") return { action, errorMessage: "Lens name is required" }

        try {
          await fetchAPI(
            `/api/unityviewer/usergroup/lenses/${organisation}/${project}/${tenant}/${product}/${groupName}/${lensName}`,
            {
              method: "DELETE",
            },
          ).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error removing lens" }
        }
      }

      case "ADD_ACCESS_TAG": {
        const tag = formData.get("tag") as string
        const restrictContributors = formData.get("restrictContributors") as string
        const usage = formData.get("usage") as string

        if (tag === "") return { action, errorMessage: "Tag name is required" }
        if (restrictContributors === "") return { action, errorMessage: "Restrict contributors is required" }
        if (usage === "") return { action, errorMessage: "Usage is required" }

        const payload = {
          name: tag,
          restrictContributors: restrictContributors === "true",
          usage,
        }

        try {
          await fetchAPI(
            `/api/unityviewer/usergroup/accessTags/${organisation}/${project}/${tenant}/${product}/${groupName}`,
            {
              method: "POST",
              body: payload,
            },
          ).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error adding access tag" }
        }
      }

      case "EDIT_ACCESS_TAG": {
        const tagName = formData.get("tagName") as string
        const restrictContributors = formData.get("restrictContributors") as string
        const usage = formData.get("usage") as string

        if (tagName === "") return { action, errorMessage: "Tag name is required" }
        if (restrictContributors === "") return { action, errorMessage: "Restrict contributors is required" }
        if (usage === "") return { action, errorMessage: "Usage is required" }

        try {
          await fetchAPI(
            `/api/unityviewer/usergroup/accessTags/${organisation}/${project}/${tenant}/${product}/${groupName}/${tagName}`,
            {
              method: "PUT",
              body: {
                restrictContributors: restrictContributors === "true",
                usage,
              },
            },
          ).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error updating access tag" }
        }
      }

      case "REMOVE_ACCESS_TAG": {
        const tagName = formData.get("tagName") as string
        if (tagName === "") return { action, errorMessage: "Tag name is required" }

        try {
          await fetchAPI(
            `/api/unityviewer/usergroup/accessTags/${organisation}/${project}/${tenant}/${product}/${groupName}/${tagName}`,
            {
              method: "DELETE",
            },
          ).then(throwIfNotOk)
          return { action }
        } catch (error) {
          return { action, errorMessage: "Error removing access tag" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch (error) {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

export function BrandUserGroups() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useGroupData()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type !== "field") return
      if (reason.name === "organisation") {
        formData.delete("project")
      }
    },
    [],
  )

  return (
    <Fragment>
      <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Brand User Groups</h1>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            {rootData.organisations.length > 1 && (
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
            )}
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
          </FiltersWrapper>
          <div className="flex-1" />
          <GroupAddNew />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function GroupRow({ group, index }: { group: BrandUserGroup; index: number }) {
  const navigate = useNavigate()
  const data = useGroupData()
  const handleConfigure = useCallback(() => {
    const groupId = `${group.tenant}::${group.product}::${group.name}`
    void navigate(`configure/${groupId}`)
  }, [navigate, group])

  return (
    <TableRow className="text-sm">
      <TableCell>{index + 1}.</TableCell>
      <TableCell className="font-medium">{group.name}</TableCell>
      <TableCell>{group.internalName ?? "—"}</TableCell>
      <TableCell>{group.tenant}</TableCell>
      <TableCell>{group.product}</TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" onClick={handleConfigure} aria-label="Configure Group">
              <ExternalLinkIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Configure Group</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <GroupDelete data={data} group={group} />
          </TooltipTrigger>
          <TooltipContent>Delete Group</TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

function Main() {
  const data = useGroupData()

  const tableContent = useMemo(() => {
    if (data?.groups == null) return

    if (data.groups.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            No groups found.
          </TableCell>
        </TableRow>
      )
    }

    return data.groups.map((group: BrandUserGroup, index: number) => (
      <GroupRow key={`group-${group.name}-${index}`} group={group} index={index} />
    ))
  }, [data?.groups])

  if (tableContent == null) {
    return <div className="py-8 text-center text-gray-500">Loading...</div>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Group Name</TableHead>
          <TableHead className="text-left">Internal Name</TableHead>
          <TableHead className="text-left">Tenant</TableHead>
          <TableHead className="text-left">Product</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

function Configuration() {
  const data = useLoaderData<typeof configLoader>()
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTab = searchParams.get("tab") ?? "lenses"

  const handleTabChange = useCallback(
    (tab: string) => {
      setSearchParams((prev: URLSearchParams) => {
        prev.set("tab", tab)
        return prev
      })
    },
    [setSearchParams],
  )

  return (
    <Fragment>
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            type="button"
            onClick={() => handleTabChange("lenses")}
            className={`flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === "lenses"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            <SearchIcon className="size-4" />
            Lenses
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("access-tags")}
            className={`flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-medium ${
              activeTab === "access-tags"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            <TagIcon className="size-4" />
            Access Tags
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-end gap-4">
        {activeTab === "lenses" && <LensAddNew />}
        {activeTab === "access-tags" && <AccessTagAddNew />}
      </div>

      {activeTab === "lenses" && <LensesTab data={data} />}
      {activeTab === "access-tags" && <AccessTagsTab data={data} />}
    </Fragment>
  )
}

function LensesTab({ data }: { data: Awaited<ReturnType<typeof configLoader>> }) {
  const tableContent = useMemo(() => {
    if (data.lenses.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="py-8 text-center text-gray-500">
            No lenses assigned to this group.
          </TableCell>
        </TableRow>
      )
    }

    return data.lenses.map((lens: ConfigGroupLens, index: number) => (
      <TableRow key={`lens-${lens.name}-${index}`} className="text-sm">
        <TableCell>{index + 1}.</TableCell>
        <TableCell className="font-medium">{lens.name}</TableCell>
        <TableCell>{lens.code}</TableCell>
        <TableCell>{lens.order}</TableCell>
        <TableCell>
          <LensDelete lens={lens} />
        </TableCell>
      </TableRow>
    ))
  }, [data.lenses])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Name</TableHead>
          <TableHead className="text-left">Code</TableHead>
          <TableHead className="text-left">Order</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

function AccessTagsTab({ data }: { data: Awaited<ReturnType<typeof configLoader>> }) {
  const tableContent = useMemo(() => {
    if (data.accessTags.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="py-8 text-center text-gray-500">
            No access tags assigned to this group.
          </TableCell>
        </TableRow>
      )
    }

    return data.accessTags.map((tag: ConfigAccessTag, index: number) => {
      const accessTagUsageIndex = ACCESS_TAG_USAGE.findIndex(
        (ct) => ct.localeCompare(tag.usage, undefined, { sensitivity: "base" }) === 0,
      )
      const usageLabel = accessTagUsageIndex !== -1 ? ACCESS_TAG_USAGE_LABELS[accessTagUsageIndex] : "—"

      return (
        <TableRow key={`tag-${tag.name}-${index}`} className="text-sm">
          <TableCell>{index + 1}.</TableCell>
          <TableCell className="font-medium">{tag.name}</TableCell>
          <TableCell>{tag.restrictContributors ? "Yes" : "No"}</TableCell>
          <TableCell>{usageLabel}</TableCell>
          <TableCell className="space-x-2">
            <AccessTagEdit tag={tag} />
            <AccessTagDelete tag={tag} />
          </TableCell>
        </TableRow>
      )
    })
  }, [data.accessTags])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Tag Name</TableHead>
          <TableHead className="text-left">Restrict Contributors</TableHead>
          <TableHead className="text-left">Usage</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

function LensAddNew() {
  const [open, setOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Add Lens</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Lens</DialogTitle>
        </DialogHeader>
        <LensAddNewForm closeDialog={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function LensAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof configAction>()
  const data = useLoaderData<typeof configLoader>()

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.action === "ADD_LENS" && fetcher.data?.errorMessage == null) {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, closeDialog])

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="ADD_LENS" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <label className="text-sm font-medium">Lens Name</label>
        <Select key={`lens-name`} required name="name">
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Lens" />
          </SelectTrigger>
          <SelectContent>
            {data.brandLenses.map((option: Lens) => (
              <SelectItem key={option.name} value={option.name}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-end gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Adding...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={fetcher.state !== "idle"}>
          Add Lens
        </Button>
      </div>
    </fetcher.Form>
  )
}

function AccessTagAddNew() {
  const [open, setOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Add Access Tag</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Access Tag</DialogTitle>
        </DialogHeader>
        <AccessTagAddNewForm closeDialog={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function AccessTagAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof configAction>()
  const data = useLoaderData<typeof configLoader>()
  const [restrictContributors, setRestrictContributors] = useState("false")
  const [usage, setUsage] = useState("both")

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.action === "ADD_ACCESS_TAG" && fetcher.data?.errorMessage == null) {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, closeDialog])

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="ADD_ACCESS_TAG" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <label className="text-sm font-medium">Tag Name</label>
        <Input name="tag" type="text" placeholder="Enter tag name" className="w-full" autoFocus required />
      </div>

      <div>
        <label className="text-sm font-medium">Restrict Contributors</label>
        <Select value={restrictContributors} onValueChange={setRestrictContributors}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="false">No</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
          </SelectContent>
        </Select>
        <input type="hidden" name="restrictContributors" value={restrictContributors} />
      </div>

      <div>
        <label className="text-sm font-medium">Usage</label>
        <Select value={usage} onValueChange={setUsage}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_TAG_USAGE.map((option: string, index: number) => (
              <SelectItem key={option} value={option}>
                {ACCESS_TAG_USAGE_LABELS[index]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" name="usage" value={usage} />
      </div>

      <div className="flex items-center justify-end gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Adding...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={fetcher.state !== "idle"}>
          Add Tag
        </Button>
      </div>
    </fetcher.Form>
  )
}

function AccessTagEdit({ tag }: { tag: ConfigAccessTag }) {
  const [open, setOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Access Tag">
          <EditIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Access Tag</DialogTitle>
        </DialogHeader>
        <AccessTagEditForm tag={tag} closeDialog={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function AccessTagEditForm({ tag, closeDialog }: { tag: ConfigAccessTag; closeDialog: () => void }) {
  const fetcher = useFetcher<typeof configAction>()
  const data = useLoaderData<typeof configLoader>()
  const [restrictContributors, setRestrictContributors] = useState(tag.restrictContributors ? "true" : "false")
  const [usage, setUsage] = useState(tag.usage)

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.action === "EDIT_ACCESS_TAG" && fetcher.data?.errorMessage == null) {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, closeDialog])

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="EDIT_ACCESS_TAG" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="tagName" value={tag.name} />

      <div>
        <label className="text-sm font-medium">Tag Name</label>
        <Input disabled value={tag.name} className="w-full bg-gray-50" />
      </div>

      <div>
        <label className="text-sm font-medium">Restrict Contributors</label>
        <Select value={restrictContributors} onValueChange={setRestrictContributors}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="false">No</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
          </SelectContent>
        </Select>
        <input type="hidden" name="restrictContributors" value={restrictContributors} />
      </div>

      <div>
        <label className="text-sm font-medium">Usage</label>
        <Select value={usage} onValueChange={setUsage}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_TAG_USAGE.map((option: string, index: number) => (
              <SelectItem key={option} value={option}>
                {ACCESS_TAG_USAGE_LABELS[index]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" name="usage" value={usage} />
      </div>

      <div className="flex items-center justify-end gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={fetcher.state !== "idle"}>
          Update Tag
        </Button>
      </div>
    </fetcher.Form>
  )
}

function LensDelete({ lens }: { lens: ConfigGroupLens }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Lens">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          Remove lens <span className="font-semibold">&quot;{lens.name}&quot;</span> from this group?
        </p>
        <LensDeleteForm lens={lens} />
      </PopoverContent>
    </Popover>
  )
}

function LensDeleteForm({ lens }: { lens: ConfigGroupLens }) {
  const fetcher = useFetcher<typeof configAction>()
  const data = useLoaderData<typeof configLoader>()

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="REMOVE_LENS" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="lensName" value={lens.name} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Removing...
        </span>
      ) : (
        fetcher.data?.action === "REMOVE_LENS" &&
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost" disabled={fetcher.state !== "idle"}>
        Confirm
      </Button>
    </fetcher.Form>
  )
}

function AccessTagDelete({ tag }: { tag: ConfigAccessTag }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Access Tag">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          Remove access tag <span className="font-semibold">&quot;{tag.name}&quot;</span> from this group?
        </p>
        <AccessTagDeleteForm tag={tag} />
      </PopoverContent>
    </Popover>
  )
}

function AccessTagDeleteForm({ tag }: { tag: ConfigAccessTag }) {
  const fetcher = useFetcher<typeof configAction>()
  const data = useLoaderData<typeof configLoader>()

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="REMOVE_ACCESS_TAG" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="tagName" value={tag.name} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Removing...
        </span>
      ) : (
        fetcher.data?.action === "REMOVE_ACCESS_TAG" &&
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost" disabled={fetcher.state !== "idle"}>
        Confirm
      </Button>
    </fetcher.Form>
  )
}

function GroupAddNew() {
  const data = useRouteLoaderData<typeof loader>(routeId)

  const [open, setOpen] = useState(false)

  const closeDialog = useCallback(() => setOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Group</DialogTitle>
        </DialogHeader>
        <GroupAddNewForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function GroupAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const data = useGroupData()!
  const [tenantProduct, setTenantProduct] = useState("")
  const [groupName, setGroupName] = useState("")

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.action === "ADD_GROUP" && fetcher.data?.errorMessage == null) {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, closeDialog])

  const availableToAdd = useMemo(() => {
    const findGroups = (group: MyGroup) => {
      return data.groups.filter(
        (g: BrandUserGroup) =>
          g.tenant.localeCompare(group.tenant, undefined, { sensitivity: "base" }) === 0 &&
          g.product.localeCompare(group.product, undefined, { sensitivity: "base" }) === 0,
      )
    }

    const result = data.availableGroups
      .map((ag: MyGroup) => {
        const matchedGroups = findGroups(ag)

        if (matchedGroups.length > 0) {
          // If a match on 'tenant' and 'product' is found, filter the 'data' array by including those that aren't already present
          const newData = ag.data.filter(
            (g: GroupData) =>
              !matchedGroups.some(
                (mg: BrandUserGroup) =>
                  g.name.localeCompare(mg.name, undefined, { sensitivity: "base", ignorePunctuation: true }) === 0,
              ),
          )

          // Return a new object with the filtered data
          return {
            ...ag,
            data: newData,
          }
        }

        // If no match is found, keep the original item
        return ag
      })
      .filter((item: MyGroup) => item.data.length > 0) // Remove items where 'data' array became empty

    return result
  }, [data.availableGroups, data.groups])

  const groups = useMemo(() => {
    if (tenantProduct.length === 0) return []
    const [tenant, product] = tenantProduct.split("::")
    const found = availableToAdd.find((ag: MyGroup) => ag.tenant === tenant && ag.product === product)
    return found?.data ?? []
  }, [tenantProduct, availableToAdd])

  return availableToAdd.length === 0 ? (
    <div className="py-8 text-center text-gray-500">All your available groups have been added</div>
  ) : (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="ADD_GROUP" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <label className="text-sm font-medium">Tenant :: Product</label>
        <Select
          value={tenantProduct}
          onValueChange={setTenantProduct}
          name="tenantProduct"
          key={`tenant-product-select-${availableToAdd.length}`} // Reset select when available groups change
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a tenant and product" />
          </SelectTrigger>
          <SelectContent>
            {availableToAdd.map((option: MyGroup) => (
              <SelectItem key={`${option.tenant}::${option.product}`} value={`${option.tenant}::${option.product}`}>
                {option.tenant} :: {option.product}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Group</label>
        <Select
          value={groupName}
          onValueChange={setGroupName}
          disabled={tenantProduct.length === 0}
          name="groupName"
          key={`group-name-select-${tenantProduct}`} // Reset select when tenantProduct changes
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={tenantProduct.length > 0 ? "Select Group" : "Select a tenant and product first"}
            />
          </SelectTrigger>
          <SelectContent>
            {groups.map((option: GroupData) => (
              <SelectItem key={option.name} value={option.name}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-end gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Adding...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={fetcher.state !== "idle"}>
          Add Group
        </Button>
      </div>
    </fetcher.Form>
  )
}

const GroupDelete = memo(function GroupDelete({
  data,
  group,
}: {
  data: ReturnType<typeof useGroupData>
  group: BrandUserGroup
}) {
  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Evidence Count">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          Delete group{" "}
          <span className="font-semibold">&quot;{`${group.tenant} :: ${group.product} :: ${group.name}`}&quot;</span>?
        </p>
        <DeleteForm data={data} group={group} />
      </PopoverContent>
    </Popover>
  )
})

function DeleteForm({ data, group }: { data: ReturnType<typeof useGroupData>; group: BrandUserGroup }) {
  const fetcher = useFetcher<typeof mainAction>()

  if (data == null) return null

  const isLoading = fetcher.state !== "idle"

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE_GROUP" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="tenant" value={group.tenant} />
      <input type="hidden" name="product" value={group.product} />
      <input type="hidden" name="groupName" value={group.name} />

      {isLoading && (
        <span className="flex items-center gap-1">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      )}
      {fetcher.data?.action === "DELETE" && fetcher.data.errorMessage != null && (
        <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

BrandUserGroups.loader = loader
BrandUserGroups.Main = Main
BrandUserGroups.action = action
BrandUserGroups.Configuration = Configuration
BrandUserGroups.configLoader = configLoader
BrandUserGroups.configAction = configAction
BrandUserGroups.routeId = routeId

Main.action = mainAction
